import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * The modal-dialog focus contract in one place. While `active`, focus is trapped inside `ref`:
 *  • the currently-focused element is remembered, and focus moves into the container (deferred one
 *    frame so an off-canvas element is on-screen before we focus it);
 *  • Tab / Shift-Tab cycle within the container;
 *  • Esc calls `onEscape`;
 *  • on deactivate, focus returns to the element that opened it;
 *  • optionally the element matched by `backgroundSelector` is marked `inert` while active, so the
 *    page behind is neither tabbable nor reachable by assistive technology.
 * Shared by the mobile nav drawer and the messaging drawer so the a11y behaviour can't drift.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  opts: { onEscape?: () => void; backgroundSelector?: string } = {},
): void {
  // Keep onEscape in a ref so a fresh callback each render doesn't re-run (and re-arm) the effect.
  const onEscapeRef = useRef(opts.onEscape);
  onEscapeRef.current = opts.onEscape;
  const { backgroundSelector } = opts;

  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    const bg = backgroundSelector ? document.querySelector<HTMLElement>(backgroundSelector) : null;
    const prevFocus = document.activeElement as HTMLElement | null;
    bg?.setAttribute('inert', '');
    // Only visible controls — the drawers hide their ☰/✕ toggles per breakpoint (display:none),
    // and focusing a hidden element is a no-op that would strand focus outside the dialog.
    const focusables = () =>
      Array.from(container?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
        (el) => el.getClientRects().length > 0,
      );
    const raf = requestAnimationFrame(() => focusables()[0]?.focus());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onEscapeRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (f.length === 0) return;
      const first = f[0]!;
      const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      bg?.removeAttribute('inert');
      prevFocus?.focus();
    };
  }, [active, ref, backgroundSelector]);
}
